import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Mail, Plus, RefreshCw, Paperclip, Send, Download, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface Props {
  employeeId?: string;
  applicantId?: string;
  defaultEmail?: string;
  recipientName?: string;
}

interface Thread {
  id: string;
  subject: string;
  recipient_email: string;
  last_message_at: string;
  gmail_thread_id: string | null;
}

interface Message {
  id: string;
  direction: "outgoing" | "incoming";
  from_address: string;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string;
  body_html: string | null;
  body_text: string | null;
  sent_at: string;
  attachments?: { id: string; filename: string; file_path: string; content_type: string | null }[];
}

export function EmailCorrespondenceTab({ employeeId, applicantId, defaultEmail, recipientName }: Props) {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    let q = supabase.from("email_threads").select("*").order("last_message_at", { ascending: false });
    if (employeeId) q = q.eq("employee_id", employeeId);
    if (applicantId) q = q.eq("applicant_id", applicantId);
    const { data, error } = await q;
    if (error) toast.error("Fehler beim Laden: " + error.message);
    setThreads((data as Thread[]) ?? []);
    setLoading(false);
  }, [employeeId, applicantId]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const loadMessages = async (threadId: string) => {
    setLoadingMessages(true);
    const { data, error } = await supabase
      .from("email_messages")
      .select("*, attachments:email_attachments(id, filename, file_path, content_type)")
      .eq("thread_id", threadId)
      .order("sent_at", { ascending: true });
    if (error) toast.error("Fehler: " + error.message);
    setMessages((data as Message[]) ?? []);
    setLoadingMessages(false);
  };

  const openThread = async (t: Thread) => {
    setSelectedThread(t);
    await loadMessages(t.id);
  };

  const syncThread = async () => {
    if (!selectedThread) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-correspondence-thread", {
        body: { thread_id: selectedThread.id },
      });
      if (error) throw error;
      toast.success(`${data?.new_messages ?? 0} neue Nachricht(en)`);
      await loadMessages(selectedThread.id);
      await loadThreads();
    } catch (e: any) {
      toast.error("Sync fehlgeschlagen: " + (e?.message ?? e));
    } finally {
      setSyncing(false);
    }
  };

  const downloadAttachment = async (path: string, filename: string) => {
    const { data, error } = await supabase.storage.from("email-correspondence").download(path);
    if (error || !data) {
      toast.error("Download fehlgeschlagen");
      return;
    }
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (selectedThread) {
    return (
      <ThreadView
        thread={selectedThread}
        messages={messages}
        loading={loadingMessages}
        syncing={syncing}
        onBack={() => { setSelectedThread(null); setMessages([]); }}
        onSync={syncThread}
        onReply={() => setReplyOpen(true)}
        onDownload={downloadAttachment}
        replyOpen={replyOpen}
        setReplyOpen={setReplyOpen}
        onSent={() => loadMessages(selectedThread.id)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Mail className="h-5 w-5" />
          E-Mail-Verlauf {recipientName ? `– ${recipientName}` : ""}
        </h3>
        <Button onClick={() => setComposeOpen(true)} disabled={!defaultEmail}>
          <Plus className="h-4 w-4 mr-2" />
          Neue E-Mail
        </Button>
      </div>

      {!defaultEmail && (
        <Card><CardContent className="p-4 text-sm text-muted-foreground">
          Keine E-Mail-Adresse hinterlegt. Bitte zuerst eine geschäftliche E-Mail im Profil eintragen.
        </CardContent></Card>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : threads.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          Noch keine E-Mails versendet.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <Card key={t.id} className="cursor-pointer hover:bg-accent/50 transition" onClick={() => openThread(t)}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.subject}</div>
                  <div className="text-sm text-muted-foreground truncate">{t.recipient_email}</div>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {format(new Date(t.last_message_at), "dd.MM.yyyy HH:mm", { locale: de })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        defaultEmail={defaultEmail || ""}
        employeeId={employeeId}
        applicantId={applicantId}
        onSent={() => { setComposeOpen(false); loadThreads(); }}
      />
    </div>
  );
}

function ThreadView({ thread, messages, loading, syncing, onBack, onSync, onReply, onDownload, replyOpen, setReplyOpen, onSent }: any) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onSync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Antworten abrufen
          </Button>
          <Button size="sm" onClick={onReply}>
            <Send className="h-4 w-4 mr-2" />
            Antworten
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-4">
        <div className="font-semibold text-lg">{thread.subject}</div>
        <div className="text-sm text-muted-foreground">{thread.recipient_email}</div>
      </CardContent></Card>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {messages.map((m: Message) => (
            <Card key={m.id} className={m.direction === "outgoing" ? "border-primary/30" : ""}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant={m.direction === "outgoing" ? "default" : "secondary"}>
                      {m.direction === "outgoing" ? "Gesendet" : "Empfangen"}
                    </Badge>
                    <span className="text-sm font-medium">{m.from_address}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(m.sent_at), "dd.MM.yyyy HH:mm", { locale: de })}
                  </span>
                </div>
                {m.body_html ? (
                  <div className="text-sm prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: sanitize(m.body_html) }} />
                ) : (
                  <pre className="text-sm whitespace-pre-wrap font-sans">{m.body_text}</pre>
                )}
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {m.attachments.map((a) => (
                      <Button key={a.id} variant="outline" size="sm" onClick={() => onDownload(a.file_path, a.filename)}>
                        <Download className="h-3 w-3 mr-1" />
                        {a.filename}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ReplyDialog
        open={replyOpen}
        onOpenChange={setReplyOpen}
        threadId={thread.id}
        onSent={() => { setReplyOpen(false); onSent(); }}
      />
    </div>
  );
}

function sanitize(html: string): string {
  return html.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/ on\w+="[^"]*"/gi, "");
}

interface ComposeProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultEmail: string;
  employeeId?: string;
  applicantId?: string;
  onSent: () => void;
}

function ComposeDialog({ open, onOpenChange, defaultEmail, employeeId, applicantId, onSent }: ComposeProps) {
  const [recipient, setRecipient] = useState(defaultEmail);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) { setRecipient(defaultEmail); setSubject(""); setBody(""); setFiles([]); } }, [open, defaultEmail]);

  const send = async () => {
    if (!recipient || !subject || !body) { toast.error("Bitte alle Pflichtfelder ausfüllen"); return; }
    setSending(true);
    try {
      // Upload attachments first
      const uploaded: { filename: string; file_path: string; content_type: string; size_bytes: number }[] = [];
      for (const f of files) {
        const path = `${employeeId || applicantId}/${crypto.randomUUID()}-${f.name}`;
        const { error } = await supabase.storage.from("email-correspondence").upload(path, f, { contentType: f.type });
        if (error) throw error;
        uploaded.push({ filename: f.name, file_path: path, content_type: f.type, size_bytes: f.size });
      }
      const { error } = await supabase.functions.invoke("send-correspondence-email", {
        body: {
          employee_id: employeeId,
          applicant_id: applicantId,
          recipient_email: recipient,
          subject,
          body_html: `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5;white-space:pre-wrap">${escapeHtml(body)}</div>`,
          attachments: uploaded,
        },
      });
      if (error) throw error;
      toast.success("E-Mail gesendet");
      onSent();
    } catch (e: any) {
      toast.error("Fehler: " + (e?.message ?? e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Neue E-Mail</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Empfänger</Label><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} /></div>
          <div><Label>Betreff</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><Label>Nachricht</Label><Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} /></div>
          <div>
            <Label>Anhänge</Label>
            <Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {files.map((f, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    <Paperclip className="h-3 w-3" />{f.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setFiles(files.filter((_, j) => j !== i))} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={send} disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReplyDialog({ open, onOpenChange, threadId, onSent }: { open: boolean; onOpenChange: (v: boolean) => void; threadId: string; onSent: () => void }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) { setBody(""); setFiles([]); } }, [open]);

  const send = async () => {
    if (!body) { toast.error("Bitte Nachricht eingeben"); return; }
    setSending(true);
    try {
      const uploaded: any[] = [];
      for (const f of files) {
        const path = `${threadId}/${crypto.randomUUID()}-${f.name}`;
        const { error } = await supabase.storage.from("email-correspondence").upload(path, f, { contentType: f.type });
        if (error) throw error;
        uploaded.push({ filename: f.name, file_path: path, content_type: f.type, size_bytes: f.size });
      }
      const { error } = await supabase.functions.invoke("send-correspondence-email", {
        body: {
          thread_id: threadId,
          body_html: `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5;white-space:pre-wrap">${escapeHtml(body)}</div>`,
          attachments: uploaded,
        },
      });
      if (error) throw error;
      toast.success("Antwort gesendet");
      onSent();
    } catch (e: any) {
      toast.error("Fehler: " + (e?.message ?? e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Antwort</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Nachricht..." />
          <div>
            <Label>Anhänge</Label>
            <Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {files.map((f, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    <Paperclip className="h-3 w-3" />{f.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setFiles(files.filter((_, j) => j !== i))} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={send} disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
